import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';

import i18n, { type AppLanguage, LANGUAGE_STORAGE_KEY } from '@/i18n';

type LanguageContextValue = {
  language: AppLanguage;
  /** true μόλις διαβαστεί το AsyncStorage (ακόμα και αν δεν έχει επιλεγεί γλώσσα) */
  languageReady: boolean;
  /** true αν ο χρήστης έχει επιλέξει γλώσσα ρητά (δεν βλέπει το language-select) */
  hasStoredLanguage: boolean;
  setLanguage: (lang: AppLanguage) => Promise<void>;
};

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

export function LanguageProvider({ children }: PropsWithChildren) {
  const [language, setLanguageState] = useState<AppLanguage>('el');
  const [languageReady, setLanguageReady] = useState(false);
  const [hasStoredLanguage, setHasStoredLanguage] = useState(false);

  useEffect(() => {
    AsyncStorage.getItem(LANGUAGE_STORAGE_KEY).then((val) => {
      if (val === 'el' || val === 'en') {
        setLanguageState(val);
        setHasStoredLanguage(true);
        void i18n.changeLanguage(val);
      }
      setLanguageReady(true);
    });
  }, []);

  const setLanguage = useCallback(async (lang: AppLanguage) => {
    setLanguageState(lang);
    setHasStoredLanguage(true);
    await AsyncStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    await i18n.changeLanguage(lang);
  }, []);

  const value = useMemo<LanguageContextValue>(
    () => ({ language, languageReady, hasStoredLanguage, setLanguage }),
    [language, languageReady, hasStoredLanguage, setLanguage],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}
